import mustache from 'mustache'
import { humanTime, maskCPFCNPJ, maskPhone } from '@/utils/common'
import { pdfService } from '../../../services/pdf.service'
import { Solicitacao } from '../../../types/interfaces/Solicitacao'
import { protocolLogo } from '../image/protocol-logo'
import { protocolGrayScaleLogo } from '../image/protocol-grayscale-logo'
import { finxLogo } from '../image/finx-logo'
import template from './template'

class AutorizacaoMateriaisPdf {
  async generate(data: Solicitacao, fileName = 'guia-autorizacao-de-materiais') {
    const rendered = mustache.render(template, this.createTemplateData(data))
    const parsed = JSON.parse(rendered)
    pdfService.generateFromDefinition(parsed, { fileName })
  }

  private createTemplateData(data: Solicitacao) {
    const {
      paciente, protocoloOperadora, procedimento,
      protocoloDeCirurgia, dataAgendamento, medicoSolicitante,
      grupoOpmeDaSolicitacao,
    } = data

    let dataAgendamentoFormatted = '-'
    if (dataAgendamento) {
      dataAgendamentoFormatted = `${humanTime(dataAgendamento)}`
    }

    let index = 0
    const materiais: TemplateData['materiais'] = []
    grupoOpmeDaSolicitacao.forEach((grupoOpme) => {
      grupoOpme.opmes.forEach((opme) => {
        let nome = ''
        const nomeDetails = [opme.opme.nomeTecnico, opme.opme.especificidade, opme.opme.tamanho, opme.opme.codigoReferencia]
        nomeDetails.forEach((elem) => {
          if (elem) {
            nome += nome !== '' ? ` - ${elem}` : elem
          }
        })

        // const fabricante = `${(opme.fornecedor1 as any).nome} - ${(opme.fornecedor1 as any).codigoAnvisa}`
        const fabricante = `${(opme.fornecedor1 as any).nome.split(' ')[0]}`
        // eslint-disable-next-line prefer-destructuring
        const quantidade = opme.quantidade
        const hisCompra = opme.codificacaoOpmeHospital ? opme.codificacaoOpmeHospital.hisIdCompra || '-' : '-'
        const hisConsignado = opme.codificacaoOpmeHospital ? opme.codificacaoOpmeHospital.hisIdConsignado || '-' : '-'
        const valor = opme.codificacaoOpmeHospital ? opme.codificacaoOpmeHospital.valor || '-' : '-'

        const rowStyle = (index % 2) === 0 ? 'rowEven' : 'rowOdd'

        materiais.push({
          nome,
          fabricante,
          quantidade,
          hisCompra,
          hisConsignado,
          valor,
          rowStyle,
          lastItem: false,
        })

        // eslint-disable-next-line no-plusplus
        index++
      })
    })

    if (materiais.length > 0) {
      materiais[materiais.length - 1].lastItem = true
    }

    const templateData = {
      hospitalLogo: finxLogo,
      protocolLogo,
      protocolGrayScaleLogo,
      paciente: {
        nome: paciente.nome,
        idade: paciente.idade,
        documento: maskCPFCNPJ(paciente.cpf),
      },
      convenio: {
        nome: paciente.convenioNome,
        plano: paciente.planoNome,
        categoria: paciente.categoriaNome,
        codigoAssociado: '-',
        senha: protocoloOperadora || '-',
      },
      cirurgia: {
        procedimentoPrincipal: `${procedimento!.descricao} - ${procedimento!.codigo}`,
        segueOProtocolo: `${protocoloDeCirurgia!.segueOProtocolo === 'SegueOProtocolo' ? 'Sim' : 'Não'}`,
        dataAgendamento: dataAgendamentoFormatted,
        medico: {
          nome: medicoSolicitante.nome,
          registro: `${medicoSolicitante.conselhoTipo} - ${medicoSolicitante.conselhoNumero}`,
          contato: maskPhone(medicoSolicitante.telefone),
        },
      },
      materiais,
      temMateriais: materiais.length > 0,
      materiaisAdicionais: [],
      temMateriaisAdicionais: false,
    } as TemplateData

    return this.sanitizeStringsInObject(templateData)
  }

  private sanitizeStringsInObject(data: any) {
    if (typeof data === 'object') {
      Object.keys(data).forEach((key: string) => {
        data[key] = this.sanitizeStringsInObject(data[key])
      })
    } else if (Array.isArray(data)) {
      data.forEach((elem, i) => {
        data[i] = this.sanitizeStringsInObject(elem)
      })
    } else if (typeof data === 'string') {
      data = this.sanitizeString(data)
    }

    return data
  }

  private sanitizeString(text: string) {
    if (text) {
      text = text.toString()
      text = text.replace(/"/gi, '\'\'')
      return text
    }
    return '-'
  }
}

interface TemplateData {
  hospitalLogo: string;
  protocolLogo: string;
  protocolGrayScaleLogo: string;
  paciente: {
    nome: string;
    idade: string;
    documento: string;
  };
  convenio: {
    nome: string;
    plano: string;
    categoria: string;
    codigoAssociado: string;
    senha: string;
  };
  cirurgia: {
    procedimentoPrincipal: string;
    segueOProtocolo: string;
    dataAgendamento: string;
    medico: {
      nome: string;
      registro: string;
      contato: string;
    },
  };
  materiais: {
    nome: string;
    fabricante: string;
    quantidade: number;
    hisCompra: string;
    hisConsignado: string;
    valor: string;
    rowStyle: string;
    lastItem: boolean;
  }[];
  temMateriais: boolean;
  materiaisAdicionais: {
    nome: string;
    fabricante: string;
    quantidade: number;
    hisCompra: string;
    hisConsignado: string;
    valor: string;
    rowStyle: string;
    lastItem: boolean;
  }[];
  temMateriaisAdicionais: boolean;
}

// eslint-disable-next-line import/prefer-default-export
export const autorizacaoMateriaisTemplate = new AutorizacaoMateriaisPdf()
